import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight } from '@fortawesome/pro-regular-svg-icons';
import Text from 'components/ui/Text';
import IconButton from 'components/ui/IconButton';
import { transparentize } from 'polished';

import React, { Fragment, useRef } from 'react';
import styled, { ThemeColors } from 'styled-components';
import Flex from './Flex';
import { ACTIVE_MULTI_MATCH, MULTI_MATCH, UNIQUE } from 'components/Orders/Order';
import { faChevronDown } from '@fortawesome/pro-light-svg-icons';

/**
 *  Table component has two different renders: as a regular table with a header row or a compact/mobile version with all cells stacked on top of each other.
 */
type CssOverflow = 'visible' | 'hidden' | 'clip' | 'scroll' | 'auto';

export type TableCell = {
    el: React.ReactNode;
    header?: boolean;
    small?: boolean;
    justify?: 'center' | 'start' | 'end';
    align?: 'left' | 'right' | 'center' | 'start' | 'end';
    splits?: number;
    hide?: boolean;
    span?: number;
    start?: number;
    overflow?: CssOverflow;
    padding?: string;
    hideCompactHeader?: boolean;
    ean?: string | null;
    sku?: string | null;
    key?: string | null;
};

export type TableRow = {
    onClick?: () => void;
    header?: boolean;
    isSplit?: boolean;
    cells: TableCell[];
    arrow?: boolean;
    arrowLabel?: string; // Label for the arrow icon, used for accessibility.
    border?: boolean;
    fillWidth?: boolean;
    faded?: boolean;
    headers?: React.ReactNode[];
    useDarkBackground?: boolean;
    key?: string;
    tintOdd?: boolean;
    bgColor?: keyof ThemeColors;
    removeGap?: boolean; // Removes gap to element above. Useful for grouping rows.
    hideCompactPadding?: boolean;
    highlightedKey?: string | null;
    modified?: boolean;
    bgModified?: keyof ThemeColors;
    highlightVariant?: 'unique' | 'multi-match' | 'active-multi-match' | null;
    className?: string;
};

const CompactTableRow = ({
    onClick,
    cells,
    arrow,
    arrowLabel,
    border = false,
    faded = false,
    bgColor,
    headers,
    tintOdd,
    hideCompactPadding,
    highlightedKey,
    modified,
    bgModified,
    highlightVariant,
}: TableRow) => {
    const isHighlighted = highlightedKey ? validateEanOrSku(cells, highlightedKey) : false;
    return (
        <CompactRow
            faded={faded}
            className={hideCompactPadding ? '' : `p-3`}
            onClick={() => onClick?.()}
            clickable={!!onClick}
            border={border}
            bgColor={bgColor}
            tintOdd={tintOdd}
            highlighted={isHighlighted}
            highlightVariant={isHighlighted ? highlightVariant : undefined}
            modified={modified}
            bgModified={bgModified}
        >
            {cells.map((c, index) =>
                c.hide ? null : (
                    <Fragment key={`cell_${index}`}>
                        {!c.hideCompactHeader && headers?.[index] && (
                            <CompactCell className={c.header ? 'f3-700' : ''} overflow={c.overflow}>
                                <Text as="span" small bold nowrap>
                                    {headers[index]}
                                </Text>
                            </CompactCell>
                        )}
                        <CompactCell
                            className={c.header ? 'f3-500' : ''}
                            overflow={c.overflow}
                            $span={!!headers?.[index] && !c.hideCompactHeader ? 1 : 2}
                        >
                            {c.small ? (
                                <Text as="span" small light nowrap>
                                    {c.el}
                                </Text>
                            ) : (
                                c.el
                            )}
                        </CompactCell>
                    </Fragment>
                ),
            )}
            {arrow && (
                <CompactArrowContainer>
                    {arrowLabel && (
                        <Text as="span" fontSize={11}>
                            {arrowLabel}
                        </Text>
                    )}
                    <ArrowIcon icon={faArrowRight} />
                </CompactArrowContainer>
            )}
        </CompactRow>
    );
};

type TableProps = {
    rows: TableRow[];
    headers?: React.ReactNode[];
    footers?: TableRow;
    compact?: boolean;
    cols?: number;
    className?: string;
    arrow?: boolean;
    tintOddRows?: boolean;
    renderCompactHeaders?: boolean;
    colLayout?: string; // specify grid col template - no effect on mobile.
    rowLayout?: string; // specify grid row template - no effect on mobile.
    useCellBorder?: boolean;
    showCellReferencing?: boolean;
    bgColor?: keyof ThemeColors;
    gap?: string; // Override gap. default is 4px
    arrowLabel?: string;
    highlightedKey?: string | null;
    highlightVariant?: typeof MULTI_MATCH | typeof UNIQUE | typeof ACTIVE_MULTI_MATCH | null;
};
const Table: React.FC<TableProps> = ({
    compact,
    headers,
    footers,
    rows,
    cols = 0,
    className,
    arrow,
    arrowLabel,
    tintOddRows = false,
    colLayout,
    rowLayout,
    useCellBorder,
    showCellReferencing,
    bgColor,
    gap,
    highlightedKey,
    highlightVariant,
}) => {
    const tableRef = useRef<HTMLDivElement>(null);

    if (compact) {
        return (
            <TableContainer className={className ? className : ''}>
                {rows.map((r, i) => (
                    <CompactTableRow
                        key={r.key || `row_${i}`}
                        onClick={r.onClick}
                        cells={r.cells}
                        arrow={arrow}
                        arrowLabel={arrowLabel}
                        border={r.border}
                        faded={r.faded}
                        headers={headers}
                        tintOdd={tintOddRows}
                        bgColor={bgColor}
                        hideCompactPadding={r.hideCompactPadding}
                        highlightedKey={highlightedKey}
                        highlightVariant={highlightVariant}
                        modified={r.modified}
                        bgModified={r.bgModified}
                        className={`${i === 0 ? 'first-row' : ''} ${i === rows.length - 1 ? 'last-row' : ''}`.trim()}
                    />
                ))}
            </TableContainer>
        );
    }

    const rowGap = gap ?? (bgColor ? '3px' : undefined);

    return (
        <TableContainer className={className ? className : ''} ref={tableRef} role="grid" tabIndex={0}>
            <StyledTable gap={rowGap} cols={cols} arrow={arrow} colLayout={colLayout} rowLayout={rowLayout}>
                {showCellReferencing &&
                    Array.from({ length: cols }).map((_, i) => (
                        <HeaderCell className="f3-500 " key={`cell_ref_${i}`} centered role="columnheader">
                            {i === 0 ? '' : String.fromCharCode(65 + (i - 1))}
                        </HeaderCell>
                    ))}
                {headers?.map((h, i) => (
                    <Fragment key={`table_header_${i}`}>
                        {i === 0 && showCellReferencing && <CellReferencing>1</CellReferencing>}
                        <HeaderCell className="f3-500" key={i} role="columnheader">
                            {h}
                        </HeaderCell>
                    </Fragment>
                ))}
                {arrow && <HeaderCell role="columnheader" /> /* Empty header cell (Arrow column) */}
                {rows.map((r, rowIdx) => {
                    const eanOrSku = validateEanOrSku(r.cells, highlightedKey);
                    const isHighlighted = Boolean(highlightedKey) && eanOrSku;
                    return (
                        <Row
                            key={`${r.key ? r.key : rowIdx}`}
                            data-row
                            onClick={() => r.onClick?.()}
                            clickable={!!r.onClick}
                            cols={r.fillWidth ? cols : undefined}
                            faded={r.faded}
                            tintOdd={tintOddRows}
                            bgColor={bgColor}
                            removeGap={r.removeGap && rowGap ? rowGap : undefined}
                            role="row"
                            modified={r.modified}
                            bgModified={r.bgModified}
                            className={`${isHighlighted ? 'highlight-row' : ''} ${rowIdx === 0 ? 'first-row' : ''} ${
                                rowIdx === rows.length - 1 ? 'last-row' : ''
                            }`.trim()}
                            tabIndex={1}
                        >
                            {showCellReferencing && <CellReferencing>{rowIdx + (headers ? 2 : 1)}</CellReferencing>}
                            {r.cells.map((c, j) => {
                                return c.hide ? null : (
                                    <RowCell
                                        data-row-cell
                                        padding={c.padding}
                                        // vPadding={bgColor ? 10 : 8}
                                        vPadding={8}
                                        tPadding={!!r.removeGap}
                                        start={c.start}
                                        span={c.span}
                                        key={`${r.key || rowIdx}_${j}`}
                                        justify={c.justify}
                                        align={c.align} // <-- Pass align here
                                        overflow={c.overflow}
                                        useBorder={useCellBorder}
                                        highlighted={isHighlighted}
                                        highlightVariant={
                                            isHighlighted ? r.highlightVariant ?? highlightVariant : undefined
                                        }
                                        role="rowcell"
                                        tabIndex={0}
                                        className={`row-cell ${isHighlighted ? 'highlight-row' : ''}`}
                                    >
                                        {c.el}
                                    </RowCell>
                                );
                            })}
                            {arrow && (
                                <RowCell useBorder={useCellBorder}>
                                    <Arrow
                                        className="cursor-pointer"
                                        iconRight
                                        icon={faArrowRight}
                                        bgColor="transparent"
                                    >
                                        {arrowLabel && (
                                            <Text as="span" fontSize={11}>
                                                {arrowLabel}
                                            </Text>
                                        )}
                                    </Arrow>
                                </RowCell>
                            )}
                        </Row>
                    );
                })}
                {footers && (
                    <Row className="f3-500" cols={footers.fillWidth ? cols : undefined}>
                        {footers.cells?.map((f, i) => (
                            <Cell start={f.start} span={f.span} key={i} useBorder={useCellBorder}>
                                {f.el}
                            </Cell>
                        ))}
                    </Row>
                )}
            </StyledTable>
        </TableContainer>
    );
};

export default Table;

export const SortableHeaderElement = ({
    label,
    active,
    desc,
    onClick,
}: {
    label: string | React.ReactNode;
    active?: boolean;
    desc?: boolean;
    onClick?: () => void;
}) => {
    return onClick ? (
        <StyledSortableHeaderElement active={active} onClick={onClick} desc={desc}>
            {label}
            <FontAwesomeIcon icon={faChevronDown} />
        </StyledSortableHeaderElement>
    ) : (
        <>{label}</>
    );
};

export const validateEanOrSku = (cells: TableCell[], highlightedKey?: string | null) => {
    if (!highlightedKey) return false;
    const key = highlightedKey.trim().toLowerCase();
    return cells.some(
        (c) => c.ean?.trim().toLowerCase() === key || c.sku?.trim().toLowerCase() === key,
    );
};

const StyledSortableHeaderElement = styled.button<{ active?: boolean; desc?: boolean }>`
    display: flex;
    background: transparent;
    justify-content: center;
    align-items: center;
    border: none;
    font-size: 12px;
    font-weight: 500;
    padding: 0;
    gap: 5px;
    cursor: pointer;
    text-decoration: ${({ active }) => (active ? 'underline' : 'none')};
    > svg {
        transform: rotate(${({ desc }) => (desc ? 0 : 180)}deg);
        opacity: ${({ active }) => (active ? 1 : 0.35)};
        transition: transform 0.15s ease, opacity 0.15s ease;
    }
`;

type StyledTableProps = {
    cols: number;
    arrow?: boolean;
    tintOddRows?: boolean;
    expandedWidth?: boolean;
    minmax?: string;
    colLayout?: string;
    rowLayout?: string;
    gap?: string;
};

const StyledTable = styled.div<StyledTableProps>`
    display: grid;
    grid-template-columns: ${({ cols, arrow, colLayout }) =>
        `${colLayout ?? `repeat(${cols}, minmax(20px, auto))`}${arrow ? ' min-content' : ''}`};
    grid-template-rows: ${({ rowLayout }) => `minmax(20px, auto) ${rowLayout ?? 'auto'}`};

    ${({ expandedWidth }) => expandedWidth && `width: calc(100% + 20px); margin-left: -10px;`}


    /* Round corners on first and last visible data rows */
    /* target first/last data row cells using explicit classes (more reliable than :first-of-type) */
    & > .first-row > div:first-child {
        border-top-left-radius: var(--radius);
    }
    & > .first-row > div:last-child {
        border-top-right-radius: var(--radius);
    }

    & > .last-row > div:first-child {
        border-bottom-left-radius: var(--radius);
    }
    & > .last-row > div:last-child {
        border-bottom-right-radius: var(--radius);
    }
`;

const TableContainer = styled.div`
    width: 100%;
`;

const Arrow = styled(IconButton)`
    flex: 1;
    height: 100% !important;
    width: 100% !important;
`;

const Cell = styled.div<{
    span?: number;
    start?: number;
    useBorder?: boolean;
    centered?: boolean;
    vPadding?: number;
    padding?: string;
    tPadding?: boolean;
}>`
  font-size: 12px;
  padding: ${({ vPadding }) => `${vPadding ?? 8}px 8px`};
  ${({ tPadding }) => tPadding && `padding-top: 0;`}
  ${({ padding }) => padding && `padding-bottom: ${padding};`}
  ${({ start }) => start && `grid-column-start: ${start};`}
  ${({ span }) => span && `grid-column-end: span ${span};`}
  ${({ useBorder }) => useBorder && `border: 0.5px solid #D3D3D3;`}
  ${({ centered }) => centered && `text-align: center;`}
`;

const Row = styled.div<{
    clickable?: boolean;
    cols?: number;
    faded?: boolean;
    tintOdd?: boolean;
    bgColor?: keyof ThemeColors;
    removeGap?: string;
    modified?: boolean;
    bgModified?: keyof ThemeColors;
}>`
  display: contents;
  cursor: ${({ clickable }) => (clickable ? 'pointer' : 'default')};

  
  ${Cell} {
      ${({ removeGap }) =>
          removeGap &&
          `
      margin-top: -${removeGap};
      height: calc(100% + ${removeGap});
      `}
    transition: all 0.1s linear;
    /* ${({ faded }) => faded && 'opacity: 0.5;'} */
    ${({ faded }) =>
        faded &&
        `
        opacity: 0.35;
        background-color: #000;
    `}
    ${({ cols }) => cols && `grid-column: span ${cols};`}
    ${({ bgColor, theme }) => bgColor && `background-color: ${theme.colors[bgColor]};`}
    ${({ theme, modified, bgModified }) =>
        modified && bgModified && `background-color: ${transparentize(0.7, theme.colors[bgModified])};`}
}

${({ tintOdd }) =>
    tintOdd &&
    `
&:nth-child(2n) {
    ${Cell} {
        background-color: rgba(0, 0, 0, 0.025);
        }
        }
        `}

  &:hover ${Cell} {
    ${({ clickable, tintOdd }) =>
        clickable && `background-color: ${tintOdd ? 'rgba(0, 0, 0, 0.05)' : 'rgba(0, 0, 0, 0.025)'};`}
  }
`;

const HeaderCell = styled(Cell)`
    font-size: 12px;
    align-self: baseline;
    padding: 0px 6px;
`;

const RowCell = styled(Cell)<{
    justify?: 'start' | 'end' | 'center';
    align?: 'left' | 'right' | 'center' | 'start' | 'end';
    overflow?: CssOverflow;
    highlighted?: boolean;
    highlightVariant?: typeof MULTI_MATCH | typeof UNIQUE | typeof ACTIVE_MULTI_MATCH | null;
    isActive?: boolean | null;
}>`
    text-overflow: ellipsis;
    display: flex;
    ${({ overflow }) => (overflow ? `overflow: ${overflow};` : 'overflow: hidden;')}
    white-space: pre-line;
    height: 100%;
    align-items: ${({ align }) =>
        align === 'start' ? 'flex-start' : align === 'end' ? 'flex-end' : align === 'center' ? 'center' : 'center'};
    ${({ justify }) => justify && `justify-content: ${justify};`}

    ${({ highlighted, highlightVariant }) =>
        highlighted &&
        (highlightVariant === UNIQUE
            ? 'background-color: rgba(122, 207, 139, 0.6) !important;'
            : highlightVariant === ACTIVE_MULTI_MATCH
            ? 'background-color: #ffd484 !important;'
            : 'background-color: #fff9c4 !important;')}

    transition: background-color 0.8s cubic-bezier(0.4,0,0.2,1);
    background-color: ${({ theme }) => theme.colors.success};

    .focused-element {
        outline: 2px solid red !important;
        z-index: 1;
    }
`;

const CompactRow = styled.div<{
    highlighted?: boolean;
    highlightVariant?: typeof UNIQUE | typeof MULTI_MATCH | typeof ACTIVE_MULTI_MATCH | null;
    selected?: boolean;
    clickable?: boolean;
    border: boolean;
    faded?: boolean;
    tintOdd?: boolean;
    bgColor?: keyof ThemeColors;
    modified?: boolean;
    bgModified?: keyof ThemeColors;
}>`
  transition: background-color 0.1s linear;
  cursor: ${({ clickable }) => (clickable ? 'pointer' : 'default')};
  border-top: ${({ theme, border }) => (border ? `1px solid ${theme.colors.borderLight};` : '')};
  display: grid;
  grid-template-columns: min-content 1fr;
  gap: 6px 1rem;
  position: relative;

  ${({ faded }) => faded && 'opacity: 0.5;'}
  ${({ bgColor, theme }) =>
      bgColor &&
      `
    background-color: ${theme.colors[bgColor]};
    margin-bottom: 3px;
  `}
  ${({ tintOdd }) =>
      tintOdd &&
      `
      &:nth-child(2n) {
        background-color: rgba(0, 0, 0, 0.025);
      }
    `}

  &:hover {
    ${({ clickable, tintOdd }) =>
        clickable && `background-color: ${tintOdd ? 'rgba(0, 0, 0, 0.05)' : 'rgba(0, 0, 0, 0.025)'};`}
  }

    &:first-of-type {
        border-top-left-radius: var(--radius);
        border-top-right-radius: var(--radius);
        overflow: hidden;
    }

    &:last-of-type {
        border-bottom-left-radius: var(--radius);
        border-bottom-right-radius: var(--radius);
        overflow: hidden;
    }

    &.first-row {
        border-top-left-radius: var(--radius);
        border-top-right-radius: var(--radius);
        overflow: hidden;
    }

    &.last-row {
        border-bottom-left-radius: var(--radius);
        border-bottom-right-radius: var(--radius);
        overflow: hidden;
    }

  ${({ modified, bgModified, theme }) =>
      modified && bgModified && `background-color: ${transparentize(0.7, theme.colors[bgModified])};`}

      ${({ highlighted, highlightVariant }) =>
          highlighted &&
          (highlightVariant === UNIQUE
              ? 'background-color: rgba(122, 207, 139, 0.6) !important;'
              : highlightVariant === ACTIVE_MULTI_MATCH
              ? 'background-color: #f09313 !important;'
              : 'background-color: #fff9c4 !important;')}

    transition: background-color 0.8s cubic-bezier(0.4,0,0.2,1);
`;

const CompactCell = styled(Flex)<{ overflow?: CssOverflow; $span?: number }>`
    padding-left: 8px;
    align-items: center;
    font-size: 12px;
    text-overflow: ellipsis;
    ${({ overflow }) => (overflow ? `overflow: ${overflow};` : 'overflow: hidden;')}
    white-space: nowrap;
    ${({ $span }) => $span && `grid-column: span ${$span};`}
`;

const CompactArrowContainer = styled.div`
    position: absolute;
    right: 12px;
    top: 8px;
    display: flex;
    align-items: center;
    gap: 4px;
`;

const ArrowIcon = styled(FontAwesomeIcon)`
    color: ${({ theme }) => theme.colors.textLight};
`;

const CellReferencing = styled.div`
    display: flex;
    align-items: center;
    justify-content: start;
    font-size: 12px;
`;
